import { formationAPI } from '../services/api';
import {
  fetchBenefitsDashboardData,
  filterPaymentsInRange,
  filterExpensesInRange,
  getPaymentDate,
  isCountedPayment,
  getPaidAmount,
  sumPaidRevenue,
  sumTeacherCommission,
  sumExpenseAmounts,
  computePeriodFinancials,
} from './benefitsFinance';
import {
  countEnrolledStudentsForCourse,
  batchBelongsToCourse,
} from './formationEnrollmentBundle';

const MONTH_LABELS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
const DAY_LABELS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

const PAYMENT_METHOD_LABELS = {
  cash: 'Espèces',
  card: 'Carte',
  transfer: 'Virement',
  check: 'Chèque',
};

/** Shared source for Rapports — same APIs as Bénéfices, Paiements, Groupes, Cours. */
export async function fetchReportsSourceData() {
  const [base, studentsRes] = await Promise.all([
    fetchBenefitsDashboardData(),
    formationAPI.getStudents({ limit: 500, page: 1 }),
  ]);
  return {
    ...base,
    students: studentsRes?.items || [],
  };
}

export function getReportsDateParams(dateRange, customStartDate, customEndDate) {
  const now = new Date();
  let startDate;
  let endDate;

  switch (dateRange) {
    case 'week': {
      const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1);
      startDate = new Date(now.getFullYear(), now.getMonth(), diff);
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      break;
    }
    case 'month':
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      break;
    case 'quarter': {
      const quarter = Math.floor(now.getMonth() / 3);
      startDate = new Date(now.getFullYear(), quarter * 3, 1);
      endDate = new Date(now.getFullYear(), (quarter + 1) * 3, 0);
      break;
    }
    case 'year':
      startDate = new Date(now.getFullYear(), 0, 1);
      endDate = new Date(now.getFullYear(), 11, 31);
      break;
    case 'custom':
      if (customStartDate && customEndDate) {
        startDate = new Date(customStartDate);
        endDate = new Date(customEndDate);
      } else {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      }
      break;
    default:
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  }

  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
    rangeStart: startDate,
    rangeEnd: (() => {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      return end;
    })(),
  };
}

function paidRevenueFromPayments(payments) {
  return sumPaidRevenue(payments);
}

function teacherCostsFromPayments(payments, commissionRate) {
  return sumTeacherCommission(payments, commissionRate);
}

function educationalCostsFromExpenses(expenses) {
  return sumExpenseAmounts(expenses);
}

export function computeFinancialReport(source, dateRange, customStartDate, customEndDate) {
  const payments = source?.payments || [];
  const expenses = source?.expenses || [];
  const commissionRate = source?.commissionRate ?? 0.4;
  const { rangeStart, rangeEnd } = getReportsDateParams(dateRange, customStartDate, customEndDate);

  const filteredPayments = filterPaymentsInRange(payments, rangeStart, rangeEnd);
  const timeSeries = [];

  if (dateRange === 'week') {
    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(rangeStart);
      dayDate.setDate(rangeStart.getDate() + i);
      const nextDay = new Date(dayDate);
      nextDay.setDate(dayDate.getDate() + 1);
      const dayPayments = filterPaymentsInRange(payments, dayDate, nextDay);
      const income = paidRevenueFromPayments(dayPayments);
      const teacherCosts = teacherCostsFromPayments(dayPayments, commissionRate);
      timeSeries.push({
        period: `${DAY_LABELS[dayDate.getDay()]} ${dayDate.getDate()}`,
        income: Math.round(income),
        expenses: Math.round(teacherCosts),
        profit: Math.round(income - teacherCosts),
      });
    }
  } else if (dateRange === 'month') {
    let weekStart = new Date(rangeStart);
    let weekNumber = 1;
    while (weekStart <= rangeEnd && weekNumber <= 5) {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      if (weekEnd > rangeEnd) weekEnd.setTime(rangeEnd.getTime());
      const weekPayments = filterPaymentsInRange(payments, weekStart, weekEnd);
      const income = paidRevenueFromPayments(weekPayments);
      const teacherCosts = teacherCostsFromPayments(weekPayments, commissionRate);
      timeSeries.push({
        period: `Sem ${weekNumber}`,
        income: Math.round(income),
        expenses: Math.round(teacherCosts),
        profit: Math.round(income - teacherCosts),
      });
      weekStart.setDate(weekStart.getDate() + 7);
      weekNumber += 1;
    }
  } else {
    const monthsToShow = dateRange === 'quarter' ? 3 : dateRange === 'year' ? 12 : 6;
    const now = new Date();
    for (let i = monthsToShow - 1; i >= 0; i--) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const nextMonth = new Date(now.getFullYear(), now.getMonth() - i + 1, 0, 23, 59, 59, 999);
      const monthPayments = filterPaymentsInRange(payments, monthDate, nextMonth);
      const monthExpenses = filterExpensesInRange(expenses, monthDate, nextMonth);
      const income = paidRevenueFromPayments(monthPayments);
      const teacherCosts = teacherCostsFromPayments(monthPayments, commissionRate);
      const educationalExpenses = educationalCostsFromExpenses(monthExpenses);
      const totalExpenses = teacherCosts + educationalExpenses;
      timeSeries.push({
        period: `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, '0')}`,
        income: Math.round(income),
        expenses: Math.round(totalExpenses),
        profit: Math.round(income - totalExpenses),
      });
    }
  }

  const period = computePeriodFinancials(
    payments,
    expenses,
    rangeStart,
    rangeEnd,
    commissionRate
  );

  const incomeByType = filteredPayments.reduce((acc, payment) => {
    if (!isCountedPayment(payment)) return acc;
    const type = payment.type || 'course';
    acc[type] = (acc[type] || 0) + getPaidAmount(payment);
    return acc;
  }, {});

  const rangeExpenses = filterExpensesInRange(expenses, rangeStart, rangeEnd);
  const expensesByType = rangeExpenses.reduce((acc, expense) => {
    const category = expense.category || 'other';
    acc[category] = (acc[category] || 0) + Number(expense.amount || 0);
    return acc;
  }, {});
  expensesByType.teacher_commissions = period.teacherCosts;

  return {
    report: {
      data: {
        summary: {
          totalIncome: Math.round(period.totalIncome),
          totalExpenses: Math.round(period.totalExpenses),
          netProfit: Math.round(period.netProfit),
          profitMargin: Math.round(period.profitMargin * 100) / 100,
        },
        timeSeries,
        benefits: { incomeByType, expensesByType },
      },
    },
  };
}

export function computeStudentsReport(source) {
  const students = source?.students || [];
  const payments = source?.payments || [];
  const enrollments = source?.enrollments || [];
  const now = new Date();
  const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  const activeStudentIds = new Set(
    enrollments.filter((e) => e.status === 'active' && e.studentId).map((e) => e.studentId)
  );
  const activeStudents = activeStudentIds.size;
  const inactiveStudents = Math.max(0, students.length - activeStudents);

  const newThisMonth = enrollments.filter((enrollment) => {
    const d = new Date(enrollment.enrollmentDate || enrollment.createdAt);
    return d >= currentMonthStart && !Number.isNaN(d.getTime());
  }).length;

  const monthlyData = [];
  for (let i = 5; i >= 0; i--) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const nextMonthDate = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
    const monthName = MONTH_LABELS[monthDate.getMonth()];

    const nouveaux = enrollments.filter((enrollment) => {
      const d = new Date(enrollment.enrollmentDate || enrollment.createdAt);
      return d >= monthDate && d < nextMonthDate && !Number.isNaN(d.getTime());
    }).length;

    const studentsWithPayments = new Set();
    payments.forEach((payment) => {
      const d = getPaymentDate(payment);
      if (d && d >= monthDate && d < nextMonthDate && isCountedPayment(payment)) {
        if (payment.studentId) studentsWithPayments.add(payment.studentId);
      }
    });

    const actifs = enrollments.filter((enrollment) => {
      if (enrollment.status !== 'active' || !enrollment.studentId) return false;
      const d = new Date(enrollment.enrollmentDate || enrollment.createdAt);
      return d < nextMonthDate && !Number.isNaN(d.getTime());
    });
    const actifsCount = new Set(actifs.map((e) => e.studentId)).size;

    monthlyData.push({
      month: monthName,
      nouveaux,
      actifs: actifsCount || studentsWithPayments.size,
      inactifs: Math.max(0, students.length - actifsCount),
    });
  }

  return {
    summary: {
      total: students.length,
      active: activeStudents,
      inactive: inactiveStudents,
      newThisMonth,
    },
    monthlyData,
  };
}

const COURSE_COLORS = [
  '#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444',
  '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1',
];

export function computeCoursesReport(source) {
  const courses = source?.courses || [];
  const payments = source?.payments || [];
  const batches = source?.batches || [];
  const enrollments = source?.enrollments || [];

  const courseAnalytics = courses.map((course) => {
    const enrolledStudents = countEnrolledStudentsForCourse(course, batches, enrollments);
    const coursePayments = payments.filter((p) => p.courseId === course.id);
    const courseRevenue = paidRevenueFromPayments(coursePayments);
    const courseBatches = batches.filter((b) => batchBelongsToCourse(b, course));

    const enrolledStudentIds = new Set();
    const batchIds = new Set(courseBatches.map((b) => b.id));
    enrollments.forEach((e) => {
      if (e.status === 'active' && batchIds.has(e.batchId)) {
        enrolledStudentIds.add(e.studentId);
      }
    });
    const completedStudents = [...enrolledStudentIds].filter((sid) => {
      const student = (source.students || []).find((s) => s.id === sid);
      return student?.status === 'completed' || student?.courseStatus === 'completed';
    }).length;

    const completionRate =
      enrolledStudents > 0 ? (completedStudents / enrolledStudents) * 100 : 0;

    return {
      id: course.id,
      name: course.name || course.title || `Cours ${course.id}`,
      category: course.category || 'other',
      enrolledStudents,
      completedStudents,
      completionRate,
      revenue: courseRevenue,
      sessionsCount: courseBatches.length,
      avgRevenuePerStudent: enrolledStudents > 0 ? courseRevenue / enrolledStudents : 0,
    };
  });

  const pieData = courseAnalytics
    .filter((course) => course.enrolledStudents > 0)
    .map((course, index) => ({
      name: course.name,
      value: course.enrolledStudents,
      color: COURSE_COLORS[index % COURSE_COLORS.length],
      revenue: course.revenue,
    }));

  const topCourses = [...courseAnalytics].sort((a, b) => b.revenue - a.revenue).slice(0, 5);
  const totalRevenue = courseAnalytics.reduce((sum, c) => sum + c.revenue, 0);
  const totalEnrollments = courseAnalytics.reduce((sum, c) => sum + c.enrolledStudents, 0);
  const avgCompletionRate =
    courses.length > 0
      ? courseAnalytics.reduce((sum, c) => sum + c.completionRate, 0) / courses.length
      : 0;

  return {
    distribution: pieData,
    totalCourses: courses.length,
    activeCourses: courses.filter((c) => c.status === 'active' || !c.status).length,
    analytics: {
      totalRevenue,
      totalEnrollments,
      avgCompletionRate,
      topCourses,
      courseDetails: courseAnalytics,
    },
  };
}

export function computeSalesReport(source, dateRange, customStartDate, customEndDate) {
  const payments = source?.payments || [];
  const courses = source?.courses || [];
  const { rangeStart, rangeEnd, startDate, endDate } = getReportsDateParams(
    dateRange,
    customStartDate,
    customEndDate
  );

  const rangePayments = filterPaymentsInRange(payments, rangeStart, rangeEnd);

  const salesByMethod = rangePayments.reduce((acc, payment) => {
    if (!isCountedPayment(payment)) return acc;
    const method = payment.paymentMethod || payment.method || 'cash';
    acc[method] = (acc[method] || 0) + getPaidAmount(payment);
    return acc;
  }, {});

  const salesByCourse = {};
  rangePayments.forEach((payment) => {
    if (!isCountedPayment(payment)) return;
    const course = courses.find((c) => c.id === payment.courseId);
    const courseName = course ? course.name || course.title : 'Cours non spécifié';
    salesByCourse[courseName] = (salesByCourse[courseName] || 0) + getPaidAmount(payment);
  });

  let monthlySales = [];

  if (dateRange === 'week') {
    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(rangeStart);
      dayDate.setDate(rangeStart.getDate() + i);
      const nextDay = new Date(dayDate);
      nextDay.setDate(dayDate.getDate() + 1);
      const dayPayments = filterPaymentsInRange(payments, dayDate, nextDay);
      const sales = paidRevenueFromPayments(dayPayments);
      const transactions = dayPayments.filter(isCountedPayment).length;
      monthlySales.push({
        month: `${DAY_LABELS[dayDate.getDay()]} ${dayDate.getDate()}`,
        sales: Math.round(sales),
        transactions,
        avgPerTransaction: transactions > 0 ? Math.round(sales / transactions) : 0,
      });
    }
  } else if (dateRange === 'month') {
    let weekStart = new Date(rangeStart);
    let weekNumber = 1;
    while (weekStart <= rangeEnd && weekNumber <= 5) {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      if (weekEnd > rangeEnd) weekEnd.setTime(rangeEnd.getTime());
      const weekPayments = filterPaymentsInRange(payments, weekStart, weekEnd);
      const sales = paidRevenueFromPayments(weekPayments);
      const transactions = weekPayments.filter(isCountedPayment).length;
      monthlySales.push({
        month: `Sem ${weekNumber}`,
        sales: Math.round(sales),
        transactions,
        avgPerTransaction: transactions > 0 ? Math.round(sales / transactions) : 0,
      });
      weekStart.setDate(weekStart.getDate() + 7);
      weekNumber += 1;
    }
  } else {
    const monthsToShow = dateRange === 'quarter' ? 3 : 6;
    const now = new Date();
    for (let i = monthsToShow - 1; i >= 0; i--) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const nextMonth = new Date(now.getFullYear(), now.getMonth() - i + 1, 0, 23, 59, 59, 999);
      const monthPayments = filterPaymentsInRange(payments, monthDate, nextMonth);
      const sales = paidRevenueFromPayments(monthPayments);
      const transactions = monthPayments.filter(isCountedPayment).length;
      monthlySales.push({
        month: MONTH_LABELS[monthDate.getMonth()],
        sales: Math.round(sales),
        transactions,
        avgPerTransaction: transactions > 0 ? Math.round(sales / transactions) : 0,
      });
    }
  }

  const totalSales = Object.values(salesByMethod).reduce((sum, amount) => sum + amount, 0);
  const totalTransactions = rangePayments.filter(isCountedPayment).length;
  const avgTransactionValue = totalTransactions > 0 ? totalSales / totalTransactions : 0;

  const paymentMethodsData = Object.entries(salesByMethod)
    .map(([method, amount]) => ({
      name: PAYMENT_METHOD_LABELS[method] || method,
      value: Math.round(amount),
      percentage: totalSales > 0 ? ((amount / totalSales) * 100).toFixed(1) : 0,
    }))
    .sort((a, b) => b.value - a.value);

  const topCourses = Object.entries(salesByCourse)
    .map(([name, revenue]) => ({ name, revenue: Math.round(revenue) }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5);

  const last = monthlySales[monthlySales.length - 1]?.sales || 0;
  const prev = monthlySales[monthlySales.length - 2]?.sales || 0;
  const salesGrowth = prev > 0 ? (((last - prev) / prev) * 100).toFixed(1) : 0;

  return {
    summary: {
      totalSales: Math.round(totalSales),
      totalTransactions,
      avgTransactionValue: Math.round(avgTransactionValue),
      period: `${startDate} à ${endDate}`,
    },
    salesByMethod: paymentMethodsData,
    salesByCourse: topCourses,
    monthlySales,
    trends: { salesGrowth },
  };
}

export function computeReportsOverview(source) {
  const payments = source?.payments || [];
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  const monthPayments = filterPaymentsInRange(payments, monthStart, monthEnd);
  const monthlyRevenue = Math.round(paidRevenueFromPayments(monthPayments));

  return {
    data: {
      totalStudents: source?.students?.length || 0,
      activeStudents: source?.activeStudents ?? 0,
      totalCourses: source?.courses?.length || 0,
      activeCourses: source?.activeCourses ?? 0,
      monthlyRevenue,
      totalPayments: payments.length,
      totalExpenses: source?.expenses?.length || 0,
    },
  };
}
